import React from 'react'
import {
  Grid,
  MaxWidthConstraint,
  PhotoHero,
  Spacer,
  Tab,
  WorkArea,
} from '@revelrygroup/components'
import { GetServerSideProps } from 'next'
import Head from 'next/head'

import { API } from '../revelry_api'
import { FuneralHome } from '../types'
import { FuneralHomeFooter } from '../components/FuneralHomeFooter'
import { FuneralHomeModel } from '../components/models/FuneralHomeModel'
import Layout from '../components/Layout'
import { routes } from '../routes'
import Image from 'next/image'
import product_data from '../product_data'

interface Props {
  activeTab: string
  funeralHome: FuneralHome | null
}

const Products: React.FC<Props> = ({ activeTab, funeralHome }) => {
  return (
    <>
      <Head>
        <title>
          Products - {funeralHome.name} - {FuneralHomeModel.headTagLocation(funeralHome)}
        </title>

        <meta
          name="description"
          content={`Casket, Vault, and Urn memorial products offered by ${funeralHome.name}`}
          key="description"
        />
      </Head>

      <Layout>
        <PhotoHero>Memorial Products</PhotoHero>

        <div className="height-100">
          <main>
            <Tab.Wrapper initialActiveTab={activeTab}>
              <WorkArea style={{ backgroundColor: 'white' }}>
                <MaxWidthConstraint>
                  <div className="centered-text">
                    <Tab.List>
                      <Tab.Item controlsContentId="caskets" href={`${routes.products}?tab=caskets`}>
                        Caskets
                      </Tab.Item>

                      <Tab.Item controlsContentId="vaults" href={`${routes.products}?tab=vaults`}>
                        Vaults
                      </Tab.Item>

                      <Tab.Item controlsContentId="urns" href={`${routes.products}?tab=urns`}>
                        Urns &amp; Keepsakes
                      </Tab.Item>
                    </Tab.List>
                  </div>

                  <Spacer verticalTop="md">
                    <Tab.Content id="caskets">
                      <Grid.Wrapper>
                        {product_data.caskets.map((casket) => (
                          <Grid.Item key={casket.title}>
                            <Grid.ImageWrapper>
                              <Image
                                alt={casket.title}
                                src={casket.imageSrc}
                                layout="fill"
                                objectFit="cover"
                                priority
                                unoptimized
                              />
                            </Grid.ImageWrapper>

                            <Grid.Title>{casket.title}</Grid.Title>
                          </Grid.Item>
                        ))}
                      </Grid.Wrapper>
                    </Tab.Content>

                    <Tab.Content id="vaults">
                      <Grid.Wrapper>
                        {product_data.vaults.map((vault) => (
                          <Grid.Item key={vault.title}>
                            <Grid.ImageWrapper>
                              <Image
                                alt={vault.title}
                                src={vault.imageSrc}
                                layout="fill"
                                objectFit="cover"
                                priority
                                unoptimized
                              />
                            </Grid.ImageWrapper>

                            <Grid.Title>{vault.title}</Grid.Title>
                          </Grid.Item>
                        ))}
                      </Grid.Wrapper>
                    </Tab.Content>

                    <Tab.Content id="urns">
                      <Grid.Wrapper>
                        {product_data.urns.map((urn) => (
                          <Grid.Item key={urn.title}>
                            <Grid.ImageWrapper>
                              <Image
                                alt={urn.title}
                                src={urn.imageSrc}
                                layout="fill"
                                objectFit="cover"
                                priority
                                unoptimized
                              />
                            </Grid.ImageWrapper>

                            <Grid.Title>{urn.title}</Grid.Title>
                          </Grid.Item>
                        ))}
                      </Grid.Wrapper>
                    </Tab.Content>
                  </Spacer>
                </MaxWidthConstraint>
              </WorkArea>
            </Tab.Wrapper>
          </main>

          <FuneralHomeFooter />
        </div>
      </Layout>
    </>
  )
}

export const getServerSideProps: GetServerSideProps<Props> = async ({ query }) => {
  const activeTab = (query?.tab || 'caskets') as string

  try {
    const funeralHome = await API.funeralHomes.load()

    return { props: { activeTab, funeralHome } }
  } catch (e) {
    return { props: { activeTab, funeralHome: null } }
  }
}

export default Products
